import { useTranslation } from 'react-i18next'
import { useState, useEffect } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { UserCircleIcon, CheckCircleIcon, MapPinIcon } from '@heroicons/react/24/outline'
import { partyDimensionsApi, type ReconciliationCandidate } from '@/api/partyDimensions'

/**
 * All'inizio dell'onboarding: se esiste già in anagrafica una scheda a nome
 * dell'utente (inserita prima che si registrasse — es. un medico esterno, un
 * socio, un contatto), gliela proponiamo e chiediamo conferma. Colleghiamo la
 * scheda al suo account SOLO se conferma che è la sua (consenso esplicito).
 *
 * Privacy: mostriamo solo nome + città/struttura, mai dati di contatto di terzi.
 *
 * onDone() viene chiamato quando l'utente ha deciso (collegato o "non sono io"),
 * o subito se non ci sono candidati: così l'onboarding prosegue senza attriti.
 */
export default function ReconciliationPrompt({
  firstName,
  lastName,
  onDone,
}: {
  firstName?: string | null
  lastName?: string | null
  onDone: () => void
}) {
  const { t } = useTranslation()
  const [dismissed, setDismissed] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['reconciliation-candidates', firstName, lastName],
    queryFn: () => partyDimensionsApi.reconciliationCandidates(firstName!.trim(), lastName!.trim()),
    enabled: !!firstName?.trim() && !!lastName?.trim(),
    staleTime: 60_000,
  })

  const confirm = useMutation({
    mutationFn: (partyId: string) => partyDimensionsApi.confirmReconciliation(partyId),
    onSuccess: (res) => {
      toast.success(
        res.doctorGranted
          ? 'Scheda collegata. Il tuo profilo medico è stato attivato.'
          : 'Scheda collegata al tuo account.',
      )
      onDone()
    },
    onError: () => {
      toast.error(t('auto.componentsOnboardingReconciliationPrompt.nonEStatoPossibileCollegareLa'))
      onDone()
    },
  })

  const candidates = data?.candidates ?? []
  const noMatch = !isLoading && data != null && candidates.length === 0

  // Nessun candidato (o dati insufficienti) → prosegui l'onboarding senza attriti.
  useEffect(() => {
    if (noMatch && !dismissed) {
      setDismissed(true)
      onDone()
    }
  }, [noMatch, dismissed, onDone])

  if (dismissed || isLoading || candidates.length === 0) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-ocean-100">
            <UserCircleIcon className="h-6 w-6 text-ocean-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Ti abbiamo riconosciuto?</h2>
            <p className="text-sm text-gray-500">
              {t('auto.componentsOnboardingReconciliationPrompt.risultaGiaUnaSchedaAQuesto')}
              al tuo account così ritrovi tutto collegato. Altrimenti, nessun problema.
            </p>
          </div>
        </div>

        <div className="space-y-2">
          {candidates.map((c: ReconciliationCandidate) => (
            <div key={c.partyId} className="flex items-center justify-between rounded-xl border border-gray-200 p-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-gray-900">{c.name}</p>
                <p className="flex items-center gap-1 text-xs text-gray-500">
                  {(c.facility || c.city) && <MapPinIcon className="h-3.5 w-3.5 shrink-0" />}
                  <span className="truncate">
                    {[c.facility, c.city].filter(Boolean).join(' · ') || c.kind}
                    {(c.facility || c.city) && c.kind ? ` — ${c.kind}` : ''}
                  </span>
                </p>
              </div>
              <button
                type="button"
                disabled={confirm.isPending}
                onClick={() => confirm.mutate(c.partyId)}
                className="ml-3 inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-ocean-600 px-3 py-2 text-sm font-semibold text-white hover:bg-ocean-500 disabled:opacity-60"
              >
                <CheckCircleIcon className="h-4 w-4" />
                {t('auto.componentsOnboardingReconciliationPrompt.siSonoIo')}
              </button>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={() => { setDismissed(true); onDone() }}
          className="mt-4 w-full py-2 text-sm text-gray-500 hover:text-gray-700"
        >
          No, sono un'altra persona
        </button>
      </div>
    </div>
  )
}
